import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { 
  Workflow,
  GitBranch,
  Play,
  Pause,
  Edit,
  Trash2,
  Plus,
  Clock,
  CheckCircle,
  AlertTriangle,
  Settings,
  Brain,
  Zap,
  BarChart3,
  Target,
  Timer,
  Activity,
  TrendingUp
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWorkflowAutomation } from '@/hooks/useWorkflowAutomation';
import { format } from 'date-fns';

interface EnterpriseWorkflow {
  id: string;
  name: string;
  description: string;
  category: 'extraction' | 'validation' | 'routing' | 'compliance' | 'integration';
  priority: 'low' | 'medium' | 'high' | 'critical';
  triggers: WorkflowTrigger[];
  steps: WorkflowStep[];
  conditions: WorkflowCondition[];
  status: 'active' | 'inactive' | 'draft' | 'archived';
  isTemplate: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  lastExecuted?: string;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  errorRate: number;
}

interface WorkflowTrigger {
  id: string;
  type: 'schedule' | 'event' | 'condition' | 'manual' | 'api';
  configuration: Record<string, any>;
  isEnabled: boolean;
}

interface WorkflowStep {
  id: string;
  name: string;
  type: 'extraction' | 'validation' | 'transformation' | 'notification' | 'integration' | 'ai_processing';
  configuration: Record<string, any>;
  order: number;
  isParallel: boolean;
  retryConfig: {
    maxRetries: number;
    retryDelay: number;
  };
}

interface WorkflowCondition {
  id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'greater_than' | 'less_than' | 'contains' | 'regex';
  value: any;
  logicalOperator?: 'and' | 'or';
}

interface WorkflowExecution {
  id: string;
  workflowId: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  duration?: number;
  stepsCompleted: number;
  totalSteps: number;
  errorMessage?: string;
  inputData: Record<string, any>;
  outputData: Record<string, any>;
}

interface WorkflowMetrics {
  totalWorkflows: number;
  activeWorkflows: number;
  totalExecutions: number;
  successfulExecutions: number;
  avgExecutionTime: number;
  throughput: number;
  uptime: number;
  errorRate: number;
}

export const EnterpriseWorkflowEngine: React.FC = () => {
  const { toast } = useToast();
  const { workflowTemplates, workflowExecutions, createWorkflow, updateWorkflow, deleteWorkflow } = useWorkflowAutomation();
  
  const [workflows, setWorkflows] = useState<EnterpriseWorkflow[]>([]);
  const [executions, setExecutions] = useState<WorkflowExecution[]>([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState<EnterpriseWorkflow | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState<Partial<EnterpriseWorkflow>>({
    name: '',
    description: '',
    category: 'extraction',
    priority: 'medium',
    triggers: [],
    steps: [],
    conditions: [],
    status: 'draft',
    isTemplate: false
  });

  // Mock enterprise workflows
  useEffect(() => {
    const mockWorkflows: EnterpriseWorkflow[] = [
      {
        id: 'wf-1',
        name: 'Intelligent Bill Processing Pipeline',
        description: 'End-to-end automated bill processing with AI validation and routing',
        category: 'extraction',
        priority: 'high',
        triggers: [
          {
            id: 'trigger-1',
            type: 'event',
            configuration: { event: 'bill_uploaded' },
            isEnabled: true
          }
        ],
        steps: [
          {
            id: 'step-1',
            name: 'AI Text Extraction',
            type: 'ai_processing',
            configuration: { models: ['gpt-4o', 'claude-3'] },
            order: 1,
            isParallel: false,
            retryConfig: { maxRetries: 3, retryDelay: 5000 }
          },
          {
            id: 'step-2',
            name: 'Data Validation',
            type: 'validation',
            configuration: { rules: ['required_fields', 'format_validation'] },
            order: 2,
            isParallel: true,
            retryConfig: { maxRetries: 2, retryDelay: 2000 }
          },
          {
            id: 'step-3',
            name: 'Smart Routing',
            type: 'transformation',
            configuration: { routing_rules: 'confidence_based' },
            order: 3,
            isParallel: false,
            retryConfig: { maxRetries: 1, retryDelay: 1000 }
          }
        ],
        conditions: [
          {
            id: 'cond-1',
            field: 'confidence_score',
            operator: 'greater_than',
            value: 80
          }
        ],
        status: 'active',
        isTemplate: false,
        version: '2.1.0',
        createdAt: '2024-01-15T10:00:00Z',
        updatedAt: '2024-01-25T14:30:00Z',
        lastExecuted: '2024-01-25T16:45:00Z',
        executionCount: 1247,
        successRate: 94.2,
        avgExecutionTime: 12.5,
        errorRate: 5.8
      },
      {
        id: 'wf-2',
        name: 'Compliance Validation Workflow',
        description: 'Automated compliance checking and reporting for regulatory requirements',
        category: 'compliance',
        priority: 'critical',
        triggers: [
          {
            id: 'trigger-2',
            type: 'schedule',
            configuration: { cron: '0 0 * * *' },
            isEnabled: true
          }
        ],
        steps: [
          {
            id: 'step-4',
            name: 'Compliance Check',
            type: 'validation',
            configuration: { frameworks: ['sox', 'pci', 'gdpr'] },
            order: 1,
            isParallel: false,
            retryConfig: { maxRetries: 1, retryDelay: 5000 }
          },
          {
            id: 'step-5',
            name: 'Generate Report',
            type: 'notification',
            configuration: { format: 'pdf', recipients: ['compliance@company.com'] },
            order: 2,
            isParallel: false,
            retryConfig: { maxRetries: 2, retryDelay: 3000 }
          }
        ],
        conditions: [],
        status: 'active',
        isTemplate: false,
        version: '1.5.0',
        createdAt: '2024-01-10T08:00:00Z',
        updatedAt: '2024-01-24T11:20:00Z',
        lastExecuted: '2024-01-25T00:00:00Z',
        executionCount: 25,
        successRate: 100,
        avgExecutionTime: 45.2,
        errorRate: 0
      },
      {
        id: 'wf-3',
        name: 'Multi-Source Data Integration',
        description: 'Integrate data from multiple sources with transformation and enrichment',
        category: 'integration',
        priority: 'medium',
        triggers: [
          {
            id: 'trigger-3',
            type: 'api',
            configuration: { endpoint: '/api/trigger-integration' },
            isEnabled: true
          }
        ],
        steps: [
          {
            id: 'step-6',
            name: 'Data Collection',
            type: 'integration',
            configuration: { sources: ['erp', 'crm', 'billing'] },
            order: 1,
            isParallel: true,
            retryConfig: { maxRetries: 3, retryDelay: 10000 }
          },
          {
            id: 'step-7',
            name: 'Data Transformation',
            type: 'transformation',
            configuration: { mapping_rules: 'standard_schema' },
            order: 2,
            isParallel: false,
            retryConfig: { maxRetries: 2, retryDelay: 5000 }
          },
          {
            id: 'step-8',
            name: 'AI Enrichment',
            type: 'ai_processing',
            configuration: { models: ['data_enrichment_v2'] },
            order: 3,
            isParallel: false,
            retryConfig: { maxRetries: 1, retryDelay: 3000 }
          }
        ],
        conditions: [
          {
            id: 'cond-2',
            field: 'data_quality_score',
            operator: 'greater_than',
            value: 85
          }
        ],
        status: 'active',
        isTemplate: true,
        version: '3.0.1',
        createdAt: '2024-01-08T12:00:00Z',
        updatedAt: '2024-01-23T16:15:00Z',
        lastExecuted: '2024-01-25T13:22:00Z',
        executionCount: 567,
        successRate: 91.7,
        avgExecutionTime: 28.3,
        errorRate: 8.3
      }
    ];

    setWorkflows(mockWorkflows);

    // Mock executions
    const mockExecutions: WorkflowExecution[] = [
      {
        id: 'exec-1',
        workflowId: 'wf-1',
        status: 'completed',
        startedAt: '2024-01-25T16:45:00Z',
        completedAt: '2024-01-25T16:45:15Z',
        duration: 15,
        stepsCompleted: 3,
        totalSteps: 3,
        inputData: { bill_id: 'bill-123', file_path: '/uploads/bill.pdf' },
        outputData: { extracted_data: {}, confidence: 92.5 }
      },
      {
        id: 'exec-2',
        workflowId: 'wf-2',
        status: 'running',
        startedAt: '2024-01-25T00:00:00Z',
        duration: 1200,
        stepsCompleted: 1,
        totalSteps: 2,
        inputData: { date_range: '2024-01-24' },
        outputData: {}
      }
    ];

    setExecutions(mockExecutions);
  }, []);

  const metrics: WorkflowMetrics = {
    totalWorkflows: workflows.length,
    activeWorkflows: workflows.filter(w => w.status === 'active').length,
    totalExecutions: workflows.reduce((sum, w) => sum + w.executionCount, 0),
    successfulExecutions: workflows.reduce((sum, w) => sum + Math.floor(w.executionCount * w.successRate / 100), 0),
    avgExecutionTime: workflows.reduce((sum, w) => sum + w.avgExecutionTime, 0) / Math.max(workflows.length, 1),
    throughput: 245, // Mock throughput
    uptime: 99.8,
    errorRate: workflows.reduce((sum, w) => sum + w.errorRate, 0) / Math.max(workflows.length, 1)
  };

  const handleCreateWorkflow = () => {
    if (!newWorkflow.name || !newWorkflow.description) {
      toast({
        title: "Missing information",
        description: "Please provide a name and description for the workflow",
        variant: "destructive"
      });
      return;
    }

    const workflow: EnterpriseWorkflow = {
      id: `wf-${Date.now()}`,
      name: newWorkflow.name,
      description: newWorkflow.description,
      category: newWorkflow.category || 'extraction',
      priority: newWorkflow.priority || 'medium',
      triggers: newWorkflow.triggers || [],
      steps: newWorkflow.steps || [],
      conditions: newWorkflow.conditions || [],
      status: 'draft',
      isTemplate: false,
      version: '1.0.0',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      executionCount: 0,
      successRate: 0,
      avgExecutionTime: 0,
      errorRate: 0
    };

    setWorkflows(prev => [workflow, ...prev]);
    setIsCreateDialogOpen(false);
    setNewWorkflow({
      name: '',
      description: '',
      category: 'extraction',
      priority: 'medium',
      triggers: [],
      steps: [],
      conditions: [],
      status: 'draft',
      isTemplate: false
    });

    toast({
      title: "Workflow created",
      description: "New enterprise workflow has been created successfully",
    });
  };

  const toggleWorkflowStatus = (workflowId: string) => {
    setWorkflows(prev => prev.map(w => 
      w.id === workflowId 
        ? { ...w, status: w.status === 'active' ? 'inactive' : 'active' }
        : w
    ));
  };

  const executeWorkflow = async (workflowId: string) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    const execution: WorkflowExecution = {
      id: `exec-${Date.now()}`,
      workflowId,
      status: 'running',
      startedAt: new Date().toISOString(),
      stepsCompleted: 0,
      totalSteps: workflow.steps.length,
      inputData: {},
      outputData: {}
    };

    setExecutions(prev => [execution, ...prev]);

    // Simulate execution
    setTimeout(() => {
      execution.status = 'completed';
      execution.completedAt = new Date().toISOString();
      execution.duration = 12;
      execution.stepsCompleted = workflow.steps.length;
      setExecutions([...executions]);

      toast({
        title: "Workflow executed",
        description: `${workflow.name} completed successfully`,
      });
    }, 3000);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'running':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'inactive':
        return <Pause className="h-4 w-4 text-gray-500" />;
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'running': return 'secondary';
      case 'inactive': return 'outline';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'destructive';
      case 'high': return 'default';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'extraction': return <Brain className="h-4 w-4" />;
      case 'validation': return <CheckCircle className="h-4 w-4" />;
      case 'routing': return <GitBranch className="h-4 w-4" />;
      case 'compliance': return <Settings className="h-4 w-4" />;
      case 'integration': return <Zap className="h-4 w-4" />;
      default: return <Workflow className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Enterprise Workflow Engine</h2>
          <p className="text-muted-foreground">
            Phase 6: Advanced workflow automation with enterprise-grade orchestration
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Settings className="h-4 w-4 mr-2" />
            Engine Settings
          </Button>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Create Workflow
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Enterprise Workflow</DialogTitle>
                <DialogDescription>
                  Design a new automated workflow for your business processes
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Workflow Name</Label>
                    <Input
                      id="name"
                      value={newWorkflow.name}
                      onChange={(e) => setNewWorkflow(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter workflow name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select 
                      value={newWorkflow.category} 
                      onValueChange={(value: any) => setNewWorkflow(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="extraction">Data Extraction</SelectItem>
                        <SelectItem value="validation">Validation</SelectItem>
                        <SelectItem value="routing">Routing</SelectItem>
                        <SelectItem value="compliance">Compliance</SelectItem>
                        <SelectItem value="integration">Integration</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newWorkflow.description}
                    onChange={(e) => setNewWorkflow(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe what this workflow does"
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority Level</Label>
                  <Select 
                    value={newWorkflow.priority} 
                    onValueChange={(value: any) => setNewWorkflow(prev => ({ ...prev, priority: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleCreateWorkflow}>
                    Create Workflow
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Engine Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Workflows</CardTitle>
            <Workflow className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{metrics.activeWorkflows}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.totalWorkflows} total workflows
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions</CardTitle>
            <Activity className="h-4 w-4 text-accent" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-accent">{metrics.totalExecutions.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              {metrics.successfulExecutions.toLocaleString()} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Execution Time</CardTitle>
            <Timer className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">{metrics.avgExecutionTime.toFixed(1)}s</div>
            <p className="text-xs text-muted-foreground">
              processing time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Uptime</CardTitle>
            <TrendingUp className="h-4 w-4 text-secondary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-secondary">{metrics.uptime}%</div>
            <p className="text-xs text-muted-foreground">
              engine availability
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Engine Tabs */}
      <Tabs defaultValue="workflows" className="space-y-4">
        <TabsList>
          <TabsTrigger value="workflows">Enterprise Workflows</TabsTrigger>
          <TabsTrigger value="executions">Live Executions</TabsTrigger>
          <TabsTrigger value="templates">Workflow Templates</TabsTrigger>
          <TabsTrigger value="monitoring">Performance Monitoring</TabsTrigger>
        </TabsList>

        <TabsContent value="workflows">
          <Card>
            <CardHeader>
              <CardTitle>Enterprise Workflows</CardTitle>
              <CardDescription>
                Manage and orchestrate complex business process workflows
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Workflow</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Executions</TableHead>
                    <TableHead>Success Rate</TableHead>
                    <TableHead>Last Executed</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workflows.map((workflow) => (
                    <TableRow 
                      key={workflow.id}
                      className={selectedWorkflow?.id === workflow.id ? 'bg-muted/30' : ''}
                      onClick={() => setSelectedWorkflow(workflow)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(workflow.category)}
                          <div>
                            <p className="font-medium">{workflow.name}</p>
                            <p className="text-xs text-muted-foreground">{workflow.description}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{workflow.category}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getPriorityColor(workflow.priority)}>
                          {workflow.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(workflow.status)}
                          <Badge variant={getStatusColor(workflow.status)}>
                            {workflow.status}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>{workflow.executionCount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{workflow.successRate.toFixed(1)}%</Badge>
                      </TableCell>
                      <TableCell>
                        {workflow.lastExecuted ? 
                          format(new Date(workflow.lastExecuted), 'MMM dd, HH:mm') : 
                          'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              executeWorkflow(workflow.id);
                            }}
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleWorkflowStatus(workflow.id);
                            }}
                          >
                            {workflow.status === 'active' ? 
                              <Pause className="h-4 w-4" /> : 
                              <CheckCircle className="h-4 w-4" />
                            }
                          </Button>
                          <Button variant="outline" size="sm">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="executions">
          <Card>
            <CardHeader>
              <CardTitle>Live Workflow Executions</CardTitle>
              <CardDescription>
                Monitor real-time workflow execution status and performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No active executions</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Execution ID</TableHead>
                      <TableHead>Workflow</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Started</TableHead>
                      <TableHead>Duration</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {executions.map((execution) => {
                      const workflow = workflows.find(w => w.id === execution.workflowId);
                      const progress = (execution.stepsCompleted / execution.totalSteps) * 100;
                      
                      return (
                        <TableRow key={execution.id}>
                          <TableCell className="font-mono text-sm">
                            {execution.id.slice(-8)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {workflow && getCategoryIcon(workflow.category)}
                              <span className="font-medium">{workflow?.name || 'Unknown'}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(execution.status)}
                              <Badge variant={getStatusColor(execution.status)}>
                                {execution.status}
                              </Badge>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-primary transition-all duration-300" 
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                                <span className="text-sm">{progress.toFixed(0)}%</span>
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {execution.stepsCompleted}/{execution.totalSteps} steps
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            {format(new Date(execution.startedAt), 'MMM dd, HH:mm:ss')}
                          </TableCell>
                          <TableCell>
                            {execution.duration ? 
                              `${execution.duration}s` : 
                              `${Math.floor((Date.now() - new Date(execution.startedAt).getTime()) / 1000)}s`
                            }
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="outline" size="sm">
                                <BarChart3 className="h-4 w-4" />
                              </Button>
                              {execution.status === 'running' && (
                                <Button variant="outline" size="sm">
                                  <Pause className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle>Workflow Templates</CardTitle>
              <CardDescription>
                Pre-built workflow templates for common enterprise scenarios
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {workflows.filter(w => w.isTemplate).map((template) => (
                  <Card key={template.id} className="cursor-pointer hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getCategoryIcon(template.category)}
                          <CardTitle className="text-base">{template.name}</CardTitle>
                        </div>
                        <Badge variant="outline">Template</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">{template.category}</Badge>
                          <Badge variant={getPriorityColor(template.priority)} className="text-xs">
                            {template.priority}
                          </Badge>
                        </div>
                        <Button size="sm" variant="outline">
                          Use Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="monitoring">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Performance Monitoring
              </CardTitle>
              <CardDescription>
                Real-time workflow engine performance metrics and insights
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="text-center p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-primary">{metrics.throughput}</div>
                    <p className="text-sm text-muted-foreground">Executions/Hour</p>
                  </div>
                  <div className="text-center p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-accent">{metrics.avgExecutionTime.toFixed(1)}s</div>
                    <p className="text-sm text-muted-foreground">Avg Execution Time</p>
                  </div>
                  <div className="text-center p-4 rounded-lg border">
                    <div className="text-2xl font-bold text-success">{metrics.uptime}%</div>
                    <p className="text-sm text-muted-foreground">System Uptime</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium">Performance Insights</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/20">
                        <h5 className="font-medium text-blue-900 dark:text-blue-100">Execution Optimization</h5>
                        <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                          Parallel processing reduced average execution time by 45%.
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/20">
                        <h5 className="font-medium text-green-900 dark:text-green-100">Error Reduction</h5>
                        <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                          Retry mechanisms decreased failure rate from 12% to 3%.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-950/20">
                        <h5 className="font-medium text-purple-900 dark:text-purple-100">Resource Efficiency</h5>
                        <p className="text-sm text-purple-700 dark:text-purple-300 mt-1">
                          Smart scheduling improved resource utilization by 60%.
                        </p>
                      </div>
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20">
                        <h5 className="font-medium text-amber-900 dark:text-amber-100">Peak Performance</h5>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          System handles 400+ concurrent workflow executions.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};